import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@material-ui/core'
import LineItemRow from './LineItemRow'
import BulkImportModal from './BulkImportModal'
import MatchSuggestionsPanel from './MatchSuggestionsPanel'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const fmt = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const LineItemsView = (props) => {
  const { projectId } = useParams()
  const history = useHistory()
  const [project, setProject] = React.useState(null)
  const [lineItems, setLineItems] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [buildingFilter, setBuildingFilter] = React.useState('')
  const [sectionFilter, setSectionFilter] = React.useState('')
  const [coverageFilter, setCoverageFilter] = React.useState('')
  const [moduleFilter, setModuleFilter] = React.useState('')
  const [modules, setModules] = React.useState([])
  const [importOpen, setImportOpen] = React.useState(false)
  const [addOpen, setAddOpen] = React.useState(false)
  const [newItem, setNewItem] = React.useState({ description: '', unit: '', quantity: '', building: '', section: '' })

  React.useEffect(() => {
    fetchProject()
    fetchLineItems()
    // Fetch modules for filter dropdown
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/modules`).then(res => {
      setModules(res.data || [])
    }).catch(() => {})
    // Pre-populate moduleFilter from URL param
    const urlModuleId = new URLSearchParams(window.location.search).get('moduleId')
    if (urlModuleId) setModuleFilter(urlModuleId)
  }, [projectId])

  const fetchProject = () => {
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}`).then(res => {
      setProject(res.data)
    }).catch(err => console.error(err))
  }

  const fetchLineItems = () => {
    setLoading(true)
    const params = {}
    if (buildingFilter) params.building = buildingFilter
    if (sectionFilter) params.section = sectionFilter
    if (coverageFilter) params.coverageStatus = coverageFilter
    if (moduleFilter) params.moduleId = moduleFilter
    axios.get(`${props.url}/api/bid-estimation/projects/${projectId}/line-items`, { params }).then(res => {
      setLineItems(res.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  React.useEffect(() => {
    if (project) fetchLineItems()
  }, [buildingFilter, sectionFilter, coverageFilter, moduleFilter])

  const sections = React.useMemo(() => {
    const unique = [...new Set(lineItems.map(i => i.section).filter(Boolean))]
    return unique.sort()
  }, [lineItems])

  const grouped = React.useMemo(() => {
    const map = {}
    lineItems.forEach(item => {
      const sec = item.section || 'Uncategorized'
      if (!map[sec]) map[sec] = []
      map[sec].push(item)
    })
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]))
  }, [lineItems])

  const summary = React.useMemo(() => {
    const total = lineItems.length
    const covered = lineItems.filter(i => i.coverageStatus === 'covered').length
    const missing = lineItems.filter(i => i.coverageStatus === 'missing').length
    const totalCost = lineItems.reduce((s, i) => s + (i.totalCost || 0), 0)
    const totalSell = lineItems.reduce((s, i) => s + (i.totalSell || 0), 0)
    return { total, covered, missing, totalCost, totalSell }
  }, [lineItems])

  const handleAddItem = () => {
    const building = newItem.building || (project?.buildings?.[0]?.name || '')
    if (!newItem.description.trim() || !building) return
    axios.post(`${props.url}/api/bid-estimation/projects/${projectId}/line-items`, {
      description: newItem.description.trim(),
      unit: newItem.unit.trim() || 'LS',
      quantity: parseFloat(newItem.quantity) || 0,
      building,
      section: newItem.section.trim()
    }).then(() => {
      setAddOpen(false)
      setNewItem({ description: '', unit: '', quantity: '', building: '', section: '' })
      fetchLineItems()
    }).catch(err => console.error(err))
  }

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}><div className="hmi-body">
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          { label: project?.name || 'Project', to: `/bid-estimation/${projectId}` },
          { label: 'Line Items' }
        ]}
        title={`Line Items${project?.name ? ' · ' + project.name : ''}`}
        actions={(
          <>
            <button className="hmi-btn hmi-btn-outline" onClick={() => setImportOpen(true)}>Import</button>
            <button className="hmi-btn hmi-btn-primary" onClick={() => setAddOpen(true)}>+ Add item</button>
          </>
        )}
      />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Module</InputLabel>
          <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} label="Module">
            <MenuItem value="">All Modules</MenuItem>
            {modules.map(m => <MenuItem key={m._id} value={m._id}>{m.scope}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Building</InputLabel>
          <Select value={buildingFilter} onChange={e => setBuildingFilter(e.target.value)} label="Building">
            <MenuItem value="">All Buildings</MenuItem>
            {(project?.buildings || []).map(b => <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Section</InputLabel>
          <Select value={sectionFilter} onChange={e => setSectionFilter(e.target.value)} label="Section">
            <MenuItem value="">All Sections</MenuItem>
            {sections.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Coverage</InputLabel>
          <Select value={coverageFilter} onChange={e => setCoverageFilter(e.target.value)} label="Coverage">
            <MenuItem value="">All</MenuItem>
            <MenuItem value="covered">Covered</MenuItem>
            <MenuItem value="partial">Partial</MenuItem>
            <MenuItem value="missing">Missing</MenuItem>
            <MenuItem value="needs_review">Needs Review</MenuItem>
          </Select>
        </FormControl>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
          Showing <b style={{ color: 'var(--ink)' }}>{lineItems.length}</b> items
        </span>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '10px 0', marginBottom: 12, fontSize: 13 }}>
        <span><b>{summary.total}</b> items</span>
        <span className="pill pill-ok"><span className="dot" />{summary.covered} covered</span>
        <span className="pill pill-risk"><span className="dot" />{summary.missing} missing</span>
        <span style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}>
          Total cost <b style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink)' }}>{fmt(summary.totalCost)}</b>
        </span>
        <span style={{ color: 'var(--ink-3)' }}>
          Total sell <b style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink)' }}>{fmt(summary.totalSell)}</b>
        </span>
      </div>

      {/* Match suggestions — auto-hides when no matches */}
      <MatchSuggestionsPanel
        projectId={projectId}
        url={props.url}
        onMatchConfirmed={fetchLineItems}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><CircularProgress /></div>
      ) : (
        <div className="hmi-panel">
          <table className="hmi-table">
            <thead>
              <tr>
                <th style={{ width: 18 }}></th>
                <th style={{ width: 36 }}>#</th>
                <th>Description</th>
                <th style={{ width: 50 }}>Unit</th>
                <th style={{ width: 60 }}>Qty</th>
                <th style={{ width: 80 }}>Unit cost</th>
                <th style={{ width: 90 }}>Total</th>
                <th style={{ width: 100 }}>Coverage</th>
                <th style={{ width: 24 }}></th>
              </tr>
            </thead>
            <tbody>
              {grouped.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 20, color: 'var(--ink-3)' }}>
                    No line items yet. Upload plans, import a bid response, or click "Add Item" to get started.
                  </td>
                </tr>
              ) : (
                grouped.map(([section, items]) => (
                  <React.Fragment key={section}>
                    <tr>
                      <td colSpan={9} style={{ padding: 0 }}>
                        <div className="section-strip">
                          <span>{section}</span>
                          <span className="strip-meta">{items.length} items · {items.filter(i => i.coverageStatus === 'covered').length} covered</span>
                          <span className="strip-total">Subtotal <b>{fmt(items.reduce((s, i) => s + (i.totalCost || 0), 0))}</b></span>
                        </div>
                      </td>
                    </tr>
                    {items.map(item => (
                      <LineItemRow
                        key={item._id}
                        item={item}
                        project={project}
                        url={props.url}
                        onRefresh={fetchLineItems}
                      />
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={() => { setImportOpen(false); fetchLineItems() }}
        project={project}
        url={props.url}
      />

      {/* Add single item dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Line Item</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" variant="outlined" size="small">
            <InputLabel>Building</InputLabel>
            <Select
              value={newItem.building}
              onChange={e => setNewItem({ ...newItem, building: e.target.value })}
              label="Building"
            >
              {(project?.buildings || []).map(b => (
                <MenuItem key={b.name} value={b.name}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Section" value={newItem.section} onChange={e => setNewItem({ ...newItem, section: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" />
          <TextField label="Description" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" required />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Unit" value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" placeholder="e.g. SF, LF, EA" />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Quantity" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: e.target.value })} fullWidth margin="normal" variant="outlined" size="small" type="number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" color="primary" disabled={!newItem.description.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </div></div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(LineItemsView)
